import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { routesPath } from '@router/routes';
import { PatientUploadStatusEnum } from '@dynamika-shared/types/status';
import { useQCTaskCommentsSlice } from '@restore/QCTaskComments/QCTaskComments.slice';
import { useQCTaskPatientSlice } from '@restore/QCTaskPatient/QCTaskPatient.slice';
import { useQCTaskSeriesSlice } from '@restore/QCTaskSeries/QCTaskSeries.slice';
import { useQCTaskSlice } from '@restore/QCTask/QCTask.slice';
import { useQCTaskUploadsListSlice } from '@restore/QCTaskUploadsList/QCTaskUploadsList.slice';
import { useClinicalTrialContextSlice } from '@restore/clinicalTrialContext/clinicalTrialContext.slice';
import { findVisitConfigById } from 'src/pages/QC/helpers/findVisitConfigById';
import { capitalize } from 'lodash';
import { shortDate } from '@dynamika-shared/utils/date';
import { useQCTaskDefinitionSlice } from '@restore/QCTaskDefinition/QCTaskDefinition.slice';
import { useQCPatientVisitsDatesSlice } from '@restore/QCPatientVisitsDates/QCPatientVisitsDates.slice';

export const useQCTaskHook = () => {
  const navigate = useNavigate();

  const { id: clinicalTrialId, taskId } = useParams<{
    id: string;
    taskId: string;
  }>();

  const [contextData] = useClinicalTrialContextSlice(state => [state.data]);

  const [fetchSeries, clearSeries] = useQCTaskSeriesSlice((state, actions) => [
    actions.fetchQCTaskSeries,
    actions.clear,
  ]);

  const [taskData, fetchTask, clearTask] = useQCTaskSlice((state, actions) => [
    state.data,
    actions.fetchQCTask,
    actions.clear,
  ]);

  const [fetchComment, clearComments] = useQCTaskCommentsSlice((_, actions) => [
    actions.fetchQCTaskComments,
    actions.QCTaskCommentsClear,
  ]);

  const [fetchUploads, clearUploads] = useQCTaskUploadsListSlice(
    (_, actions) => [actions.fetchQCTaskUploadsList, actions.clear]
  );

  const [
    patientData,
    patientDataIsLoading,
    fetchPatient,
    clearPatient,
    changePatientStatus,
  ] = useQCTaskPatientSlice((state, actions) => [
    state.data,
    state.loading,
    actions.fetchQCTaskPatient,
    actions.clear,
    actions.changePatientStatus,
  ]);

  const [fetchTaskDefinitions, clearTaskDefinitions] = useQCTaskDefinitionSlice(
    (_, actions) => [actions.fetchQCTaskDefinitions, actions.clear]
  );
  const [patientVisitsDatesLoading, fetchQCPatientVisitsDates] =
    useQCPatientVisitsDatesSlice((state, actions) => [
      state.loading,
      actions.fetchQCPatientVisitsDates,
    ]);
  const visitName = useMemo(() => {
    if (taskData?.visitDefinitionId) {
      const regularVisit = findVisitConfigById(
        contextData,
        taskData?.visitDefinitionId ?? ''
      );
      return regularVisit?.name;
    }

    const unscheduledVisit = patientData?.visits?.find(
      visit => visit.id === taskData?.visitId
    );

    if (!unscheduledVisit) {
      return undefined;
    }
    if (!unscheduledVisit?.earlyTerminated) {
      return `${capitalize(unscheduledVisit?.type)} • ${shortDate(unscheduledVisit?.visitName ?? '')}`;
    }
    return unscheduledVisit?.visitName;
  }, [
    contextData,
    patientData?.visits,
    taskData?.visitDefinitionId,
    taskData?.visitId,
  ]);

  const [selectedPatientStatus, setSelectedPatientStatus] = useState<
    PatientUploadStatusEnum | ''
  >('');

  const fetchQCPatientVisits = useCallback(() => {
    if (clinicalTrialId && patientData?.id) {
      fetchQCPatientVisitsDates({
        clinicalTrialId,
        patientId: patientData?.id,
      });
    }
  }, [clinicalTrialId, patientData?.id]);
  useEffect(() => {
    if (clinicalTrialId && taskId) {
      fetchPatient({ clinicalTrialId, taskId });
    }
    return () => {
      clearPatient();
    };
    // do not refetch patient when taskId is changing because patient is the same for all visits
  }, [clinicalTrialId]);

  useEffect(() => {
    fetchQCPatientVisits();
  }, [clinicalTrialId, patientData?.id]);

  useEffect(() => {
    if (clinicalTrialId && taskId) {
      fetchTask({ clinicalTrialId, taskId });
      fetchComment({ clinicalTrialId, taskId });
      fetchUploads({ clinicalTrialId, taskId });
      fetchSeries({ clinicalTrialId, taskId });
    }
    return () => {
      clearTask();
      clearComments();
      clearUploads();
      clearSeries();
    };
  }, [clinicalTrialId, taskId]);

  useEffect(() => {
    if (clinicalTrialId && taskData?.patientId && taskData?.visitId) {
      fetchTaskDefinitions({
        clinicalTrialId,
        patientId: taskData.patientId || '',
        visitId: taskData.visitId || '',
      });
    }
    return () => {
      clearTaskDefinitions();
    };
  }, [clinicalTrialId, taskData?.patientId, taskData?.visitId]);

  const goToTasks = () => navigate(`${routesPath.qc}/${clinicalTrialId}`);

  const resetPatientStatus = () => {
    setSelectedPatientStatus('');
  };

  const handleChangePatientStatus = () => {
    if (clinicalTrialId && taskId && selectedPatientStatus) {
      changePatientStatus({
        clinicalTrialId,
        taskId,
        status: selectedPatientStatus,
      });
    }
    resetPatientStatus();
  };

  const directChangePatientStatus = (status: PatientUploadStatusEnum) => {
    if (clinicalTrialId && taskId) {
      changePatientStatus({ clinicalTrialId, taskId, status });
    }
    resetPatientStatus();
  };

  return {
    goToTasks,
    clinicalTrialId,
    taskId,
    taskData,
    patientData,
    patientDataIsLoading,
    contextData,
    closeModal: resetPatientStatus,
    showModal: !!selectedPatientStatus,
    confirmChangeStatus: (status: PatientUploadStatusEnum) =>
      setSelectedPatientStatus(status),
    changePatientStatus: handleChangePatientStatus,
    directChangePatientStatus,
    selectedPatientStatus,
    visitName,
    fetchQCPatientVisits,
    patientVisitsDatesLoading,
  };
};
